import { toast } from 'sonner'
import type { PermissionTuple } from '~/utils/ability'
import { describePermission } from '~/utils/permissionLabels'
import { getRbacMissingPermissions, resolveMutationErrorToast } from '~/utils/rbacErrors'

export { getRbacMissingPermissions }

/**
 * Toast d'erreur de mutation (`resolveMutationErrorToast`) : refus RBAC → droits manquants, titrés
 * par l'explication du backend ou le message d'échec de l'action ; sinon message de la réponse ou
 * `fallback`. Appelé automatiquement par le `MutationCache` pour toute mutation déclarant
 * `meta.errorMessage`.
 */
export const toastMutationError = (error: unknown, fallback: string) => {
  const resolved = resolveMutationErrorToast(error, fallback)
  if (resolved.kind === 'rbac') toastRbacForbidden(resolved.missingPermissions, resolved.title)
  else toast.error(resolved.message)
}

/**
 * Toast de refus RBAC, avec la liste des droits exacts à obtenir. L'`id` sonner est dérivé des
 * tuples manquants : plusieurs requêtes refusées pour les mêmes droits (typique au chargement d'un
 * écran) ne produisent qu'un seul toast.
 */
export const toastRbacForbidden = (
  missingPermissions: PermissionTuple[],
  title = 'Droits insuffisants pour charger certaines informations',
) => {
  const described = missingPermissions.map(describePermission)
  const id = `rbac-forbidden:${described
    .map((permission) => permission.key)
    .sort()
    .join(',')}`

  toast.error(title, {
    id,
    description: (
      <ul className="flex flex-col gap-0.5">
        {described.map((permission) => (
          <li key={permission.key}>
            <span className="font-medium">{permission.actionLabel}</span>
            {' — '}
            {permission.segments.join(' › ')}
          </li>
        ))}
      </ul>
    ),
  })
}
